import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Checkbox } from 'primereact/checkbox';
import PrimeDialog from '../../../components/dialog/PrimeDialog';

/**
 * 排放源選擇對話框組件
 * 功能：提供排放源列表選擇，支援關鍵字搜尋和氣體排放源過濾
 * 使用場景：在 AddItem 組件中選擇排放源時使用
 */



const EmissionSourceDialog = ({ 
    visible,        // 控制對話框顯示/隱藏
    onClose,        // 關閉對話框的回調函數
    onConfirm,      // 選擇排放源後的回調函數，會傳入選中的排放源資料
    sourceList = [], // 排放源資料列表，來自父組件
    title,          // 對話框標題，可選，預設使用多國語系標題
    currentSourceName = '' // 當前選中的排放源名稱，用於預設搜尋關鍵字
}) => {
    const { t } = useTranslation();
    
    // 如果沒有傳入 title，使用預設的多國語系標題
    const defaultTitle = `${t('emission_source')} ${t('coefficient')}`;
    const displayTitle = title || defaultTitle;
    
    // 狀態管理
    const [filteredSourceList, setFilteredSourceList] = useState([]);        // 過濾後的排放源列表
    const [searchKeyword, setSearchKeyword] = useState('');                  // 搜尋關鍵字
    const [selectedGasRateSources, setSelectedGasRateSources] = useState([]); // 選中的氣體排放源類型

    /**
     * 初始化對話框資料
     * 當對話框顯示或資料來源變更時執行
     */
    useEffect(() => {
        if (visible) {
            // 將當前選中的排放源名稱設為搜尋關鍵字
            setSearchKeyword(currentSourceName);
            
            setFilteredSourceList(sourceList);
            // 初始化選中所有 gasRateSource，預設全選
            const uniqueGasRateSources = [...new Set(sourceList.map(item => item.gasRateSource).filter(Boolean))];
            setSelectedGasRateSources(uniqueGasRateSources);
            
            // 如果有當前排放源名稱，直接執行過濾邏輯
            if (currentSourceName.trim()) {
                // 直接過濾，不依賴 handleSearch 函數
                let filtered = sourceList;
                
                // 根據當前排放源名稱過濾
                filtered = filtered.filter(item =>
                    item.sourceName?.toLowerCase().includes(currentSourceName.toLowerCase())
                );
                
                // 根據選中的 gasRateSource 過濾
                if (uniqueGasRateSources.length > 0) {
                    // 顯示選中類型的排放源 + 沒有 gasRateSource 的項目
                    filtered = filtered.filter(item =>
                        !item.gasRateSource || uniqueGasRateSources.includes(item.gasRateSource)
                    );
                }
                
                setFilteredSourceList(filtered);
            }
        }
    }, [visible, sourceList, currentSourceName]);

    /**
     * 關閉對話框處理函數
     * 清空搜尋關鍵字和選中的氣體排放源，然後關閉對話框
     */
    const handleClose = () => {
        setSearchKeyword('');
        setSelectedGasRateSources([]);
        onClose();
    };

    /**
     * 氣體排放源過濾選擇處理函數
     * @param {string} gasRateSource - 氣體排放源代碼
     * @param {boolean} checked - 是否選中
     */
    const handleGasRateSourceChange = (gasRateSource, checked) => {
        // 計算新的選中狀態
        let newSelectedGasRateSources;
        if (checked) {
            newSelectedGasRateSources = [...selectedGasRateSources, gasRateSource];
        } else {
            newSelectedGasRateSources = selectedGasRateSources.filter(item => item !== gasRateSource);
        }
        
        // 更新狀態
        setSelectedGasRateSources(newSelectedGasRateSources);
        
        // 根據新的選中狀態立即過濾列表
        let filtered = sourceList;
        
        // 根據搜尋關鍵字過濾（支援排放源名稱搜尋）
        if (searchKeyword.trim()) {
            filtered = filtered.filter(item =>
                item.sourceName?.toLowerCase().includes(searchKeyword.toLowerCase())
            );
        }
        
        // 根據新的選中狀態過濾
        if (newSelectedGasRateSources.length === 0) {
            // 如果沒有選中任何類型，只顯示沒有 gasRateSource 的項目
            filtered = filtered.filter(item => !item.gasRateSource);
        } else {
            // 顯示選中類型的排放源 + 沒有 gasRateSource 的項目
            filtered = filtered.filter(item =>
                !item.gasRateSource || newSelectedGasRateSources.includes(item.gasRateSource)
            );
        }
        
        setFilteredSourceList(filtered);
    };

    /**
     * 搜尋過濾處理函數
     * 根據關鍵字和選中的氣體排放源類型過濾排放源列表
     * 避免即時搜尋造成的畫面閃跳，只在用戶主動觸發時執行
     */
    const handleSearch = () => {
        // 根據搜尋關鍵字和選中的 gasRateSource 過濾排放源列表
        let filtered = sourceList;
        
        // 根據搜尋關鍵字過濾（支援排放源名稱搜尋）
        if (searchKeyword.trim()) {
            filtered = filtered.filter(item =>
                item.sourceName?.toLowerCase().includes(searchKeyword.toLowerCase())
            );
        }
        
        // 根據選中的 gasRateSource 過濾
        if (selectedGasRateSources.length === 0) {
            // 如果沒有選中任何類型，只顯示沒有 gasRateSource 的項目
            filtered = filtered.filter(item => !item.gasRateSource);
        } else {
            // 顯示選中類型的排放源 + 沒有 gasRateSource 的項目
            filtered = filtered.filter(item =>
                !item.gasRateSource || selectedGasRateSources.includes(item.gasRateSource)
            );
        }
        
        setFilteredSourceList(filtered);
    };



    return (
        <PrimeDialog
            scrollByBody={true}      // 啟用對話框內容區域的捲動
            visible={visible}         // 控制對話框顯示/隱藏
            isShowConfBtn={false}    // 不顯示確認按鈕，使用表格內的行動按鈕
            onClose={handleClose}    // 關閉對話框的回調
            onConfirm={() => {}}     // 空函數，實際確認邏輯在表格行中
            title={displayTitle}     // 對話框標題
            size="lg"                // 大尺寸對話框
            bodyTemplate={
                <>
                    <div className="pt-1 px-4 pb-4">
                        {/* 關鍵字搜尋區域 */}
                        <div className="col-12 lg:col-12 mb-3">
                            <div className="flex flex-column gap-2">
                                <label className="control-label">{t('keyword')}</label>
                                <input
                                    type="text"
                                    className="p-inputtext w-full"
                                    placeholder=""
                                    value={searchKeyword}
                                    onChange={(e) => setSearchKeyword(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            handleSearch();
                                        }
                                    }}
                                />
                            </div>
                        </div>

                        {/* 氣體排放源過濾選擇區域 */}
                        <div className="col-12 lg:col-12 mb-3">
                            <div className="flex flex-wrap gap-3">
                                {/* 動態生成氣體排放源過濾選項 */}
                                {[...new Set(sourceList.map(item => item.gasRateSource).filter(Boolean))].map((gasRateSource) => {
                                    // 獲取氣體排放源的名稱，如果沒有名稱則使用代碼
                                    const gasRateSourceName = sourceList.find(item => item.gasRateSource === gasRateSource)?.gasRateSourceName || gasRateSource;
                                    return (
                                        <div key={gasRateSource} className="flex align-items-center gap-2">
                                            <Checkbox
                                                inputId={gasRateSource}
                                                checked={selectedGasRateSources.includes(gasRateSource)}
                                                onChange={(e) => handleGasRateSourceChange(gasRateSource, e.checked)}
                                            />
                                            <label htmlFor={gasRateSource} className="ml-2">
                                                {gasRateSourceName}
                                            </label>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                                                {/* 排放源列表表格區域 */}
                        <div className="table-section">
                            <DataTable
                                value={filteredSourceList}        // 顯示過濾後的排放源列表
                                stripedRows                     // 啟用條紋行樣式
                                className="coefficient-table hover-highlight no-shadow"  // 使用係數表格樣式
                                tableStyle={{ minWidth: filteredSourceList.some(item => item.gasRateSource) ? '50rem' : '35rem' }}  // 動態調整表格寬度
                                emptyMessage={t('no_rec')}      // 無資料時的提示訊息
                            >
                                {/* 條件顯示氣體排放源欄位 - 只有當資料中有 gasRateSource 時才顯示 */}
                                {filteredSourceList.some(item => item.gasRateSource) && (
                                    <Column
                                        field="gasRateSourceName"
                                        header="氣體排放源"
                                        style={{ minWidth: '200px' }}
                                        body={(rowData) => (
                                            <span>{rowData.gasRateSourceName || rowData.gasRateSource || ''}</span>
                                        )}
                                    />
                                )}
                                {/* 排放源名稱欄位 */}
                                <Column
                                    field="sourceName"
                                    header={t('emission_source')}
                                    style={{ minWidth: '400px' }}
                                />
                                {/* 動作欄位 - 提供選擇按鈕 */}
                                <Column 
                                    style={{ width: 'auto' }} 
                                    body={(rowData) => (
                                        <Button
                                            rounded text severity="secondary"
                                            className="p-button-icon-only"
                                            tooltipOptions={{ position: 'top' }}
                                            tooltip={t('button.apply')}
                                            onClick={() => onConfirm(rowData)}
                                        >
                                            <i className="pi pi-check" />
                                        </Button>
                                    )} 
                                    header={t('action')} 
                                />
                            </DataTable>
                        </div>
                    </div>
                </>
            }
        />
        
    );
};

export default EmissionSourceDialog;
